// src/components/Dices/CustomDiceResults.jsx
import React, { useMemo, useEffect } from 'react';
import { Button } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useRecoilState, useRecoilValue } from 'recoil';
import { v4 as uuidv4 } from 'uuid';
import { useTranslation } from 'react-i18next';

import { DICE_TYPES } from './../../common/constants';
import {
	customDicesState,
	customDiceSelectedState,
	dicesBonusListState,
	dicesSelectedState,
	alienPushContextState,
	alienRollContextState,
	isNextDiceExplosiveState,
	isNextDiceRepetitionState,
	repeatDicesState,
} from '../../common/recoil/GameSystem/gameSystemAtom';
import { userNameState, diceResultsState, diceRollState, characterState } from '../../common/recoil/UserInfo/atoms';
import { diceRollMetaState } from '../../common/recoil/GameSystem/gameSystemAtom';
import {
	DEFAULT_DICE_COLOR,
	DEFAULT_DICE_LABEL,
	isSelectedDie,
	normalizeDiceLabel,
	toSelectedDie,
} from '../../common/services/diceMetadata';
import { updateCharacter } from '../../common/services/characterRequests';
import { getAlienPanicEntry } from '../../common/gameSystems/alien/panicTable';
import { getAlienResultPresentation } from '../../common/gameSystems/alien/formatAlienResult';
import { isAlienCharacter } from '../../common/gameSystems/registry';

const CustomDiceResults = ({ result }) => {
	const { enqueueSnackbar, closeSnackbar } = useSnackbar();
	const { t, i18n } = useTranslation();

	// ── Recoil state hooks ──────────────────────────────────────────────────────
	const [customDices] = useRecoilState(customDicesState);
	const [customDiceSelected] = useRecoilState(customDiceSelectedState);
	const [dicesBonusList] = useRecoilState(dicesBonusListState);
	const [diceResults, setDiceResults] = useRecoilState(diceResultsState);
	const [isNextDiceExplosive, setIsNextDiceExplosive] = useRecoilState(isNextDiceExplosiveState);
	const [isNextDiceRepetition, setIsNextDiceRepetition] =
		useRecoilState(isNextDiceRepetitionState);
	const [repeatDices] = useRecoilState(repeatDicesState);
	const [roll, setRoll] = useRecoilState(diceRollState);
	const [diceRollMeta, setDiceRollMeta] = useRecoilState(diceRollMetaState);
	const [alienPushContext, setAlienPushContext] = useRecoilState(alienPushContextState);
	const [alienRollContext, setAlienRollContext] = useRecoilState(alienRollContextState);
	const [character, setCharacter] = useRecoilState(characterState);
	const userName = useRecoilValue(userNameState);

	// ── Grab the JSON config for whichever custom system is selected ───────────
	const currentCustomDiceConfig = useMemo(
		() => customDices.find((d) => d.name === customDiceSelected) || { input: [], output: [] },
		[customDices, customDiceSelected]
	);

	// ── FALLBACK HELPERS: pull modifier or errorRange from recoil OR from JSON defaults
	const getMod = (name) => {
		const bonusEntry = dicesBonusList.find((b) => b.name === name);
		if (bonusEntry) return bonusEntry.value;
		const inputEntry = currentCustomDiceConfig.input.find((i) => i.name === name);
		return inputEntry?.value ?? 0;
	};
	const getErrorRange = (name) => {
		const bonusEntry = dicesBonusList.find((b) => b.name === name);
		if (bonusEntry && bonusEntry.errorRange != null) return bonusEntry.errorRange;
		const inputEntry = currentCustomDiceConfig.input.find((i) => i.name === name);
		return inputEntry?.errorRange ?? 0;
	};

	const getDiceQuantity = (input) => {
		if (Array.isArray(input.quantityFromSum)) {
			const total = input.quantityFromSum.reduce((sum, sourceName) => {
				return sum + (parseInt(String(getMod(sourceName) || 0), 10) || 0);
			}, 0);
			return Math.max(0, total);
		}

		if (input.quantityFrom) {
			return Math.max(0, parseInt(String(getMod(input.quantityFrom) || 0), 10) || 0);
		}

		return input.quantity || 1;
	};

	const getConfiguredDiceList = () =>
		currentCustomDiceConfig.input
			.filter((input) => input.type === 'dice')
			.flatMap((input) => {
				const quantity = getDiceQuantity(input);
				return Array.from({ length: quantity }, () => ({
					notation: input.value,
					color: input.color || DEFAULT_DICE_COLOR,
					label: normalizeDiceLabel(input.label || DEFAULT_DICE_LABEL),
				}));
			});

	// ── Generic utility functions ──────────────────────────────────────────────
	const sortArray = (arr) => [...arr].sort((a, b) => a - b);
	const max = (arr) => sortArray(arr)[arr.length - 1];
	const min = (arr) => sortArray(arr)[0];
	const median = (arr, low = false) => {
		const s = sortArray(arr);
		let m = Math.floor(s.length / 2);
		if (low) m -= 1;
		return s[m];
	};
	const hasExact = (arr, v) => arr.includes(v);
	const sumGroups = (...groups) => {		
		const total = groups.reduce((total, group) => {
			const groupSum = group.reduce((sum, value) => sum + value, 0);
			console.log('Current group:', group, 'Group sum:', groupSum); // Log each group and its sum
			return total + groupSum;
		}, 0);		
		return total;
	};

	// ── Exploding‐dice helpers (unchanged) ─────────────────────────────────────
	const onExplodeDices = (key, explosive) => {
		closeSnackbar(key);
		setIsNextDiceExplosive(true);
		setRoll(explosive);
	};
	const showExplosiveMessage = (results, diceDefinitions, values) => {
		const matches = results
			.map((r, i) => (values.includes(r) ? diceDefinitions[i] : null))
			.filter(Boolean);
		if (matches.length) {
			setTimeout(() => {
				enqueueSnackbar(`Hay ${matches.length} dados relanzables. ¿Quieres lanzarlos?`, {
					variant: 'success',
					anchorOrigin: { vertical: 'top', horizontal: 'center' },
					action: (key) => <Button onClick={() => onExplodeDices(key, matches)}>Lanzar</Button>,
				});
			}, 1500);
		}
	};

	// ── Core rule appli­ers (unchanged) ─────────────────────────────────────────
	const applyOutputDiceAction = (cur, results, rule, diceDefinitions) => {
		let found;
		switch (rule.action) {
			case 'find':
				switch (rule.condition) {
					case 'max': found = max(results); break;
					case 'min': found = min(results); break;
					case 'middle': found = median(results); break;
					case 'middleH': found = median(results); break;
					case 'middleL': found = median(results, true); break;
					case 'exactNumber':
						found = hasExact(results, rule.value) ? rule.value : cur.value;
						break;
				}
				cur.value = found;
				break;
			case 'explode':
				showExplosiveMessage(results, diceDefinitions, rule.values || []);
				break;
			case 'sum':
				if (rule.condition === 'all') {
					found = sumGroups(results); // Only use current
				} else if (rule.condition === 'prevCustom') {
					const prev = diceResults
						.filter((d) => d.type === DICE_TYPES.CUSTOM)
						.map((d) => d.value)[0];
					found = sumGroups(results, [prev]);
				}
				cur.value = found;
				break;

		}
		return cur;
	};

	const applyOutputModifier = (cur, action, name) => {
		const mod = getMod(name);
		const err = getErrorRange(name);
		try {
			if (action === 'apply') {
				const op = dicesBonusList.find((b) => b.name === name)?.operation || '+';
				cur.value = eval(`${mod}${op}${cur.value}`);
			}
			if (action === 'difficulty') {
				cur.success = cur.value >= mod;
				cur.partialSuccess = cur.value >= mod - err;
			}
		} catch (e) {
			console.error('Modifier error', e);
		}
		return cur;
	};

	const clickableDices = (cur, action) => {
		if (action === 'repeat') cur.repeatable = true;
		return cur;
	};

	const getAlienSummary = (diceDefinitions, values) => {
		const isPushedRoll = Boolean(alienPushContext?.pushed);
		const summary = diceDefinitions.reduce(
			(acc, die, index) => {
				const value = values[index];
				if (value === 6) {
					acc.successes += 1;
				}
				if (die?.label === 'stress' && value === 1) {
					acc.stressOnes += 1;
				}
				return acc;
			},
			{ successes: 0, stressOnes: 0 }
		);

		if (alienPushContext?.pushed) {
			summary.successes += alienPushContext.retainedSuccesses;
		}

		const panic = summary.stressOnes > 0;
		const successLabel = t(
			summary.successes === 1
				? 'diceResults.alien.successSingular'
				: 'diceResults.alien.successPlural'
		);
		const baseSummaryText = panic
			? t('diceResults.alien.summaryPanic', {
					count: summary.successes,
					successLabel,
			  })
			: t('diceResults.alien.summary', {
					count: summary.successes,
					successLabel,
			  });
		const summaryText = isPushedRoll
			? t('diceResults.alien.pushedPrefix', { summary: baseSummaryText })
			: baseSummaryText;

		return {
			successes: summary.successes,
			stressOnes: summary.stressOnes,
			panic,
			summaryText,
			isPushedRoll,
		};
	};

	const getAlienPushContext = (diceDefinitions, values, date) => {
		const retainedDiceEntries = [];
		const rerollDice = [];
		let retainedSuccesses = 0;

		diceDefinitions.forEach((die, index) => {
			const value = values[index];
			const normalizedDie = {
				notation: die.notation,
				color: die.color || DEFAULT_DICE_COLOR,
				label: normalizeDiceLabel(die.label || DEFAULT_DICE_LABEL),
			};

			if (value === 6) {
				retainedSuccesses += 1;
				retainedDiceEntries.push({
					id: uuidv4(),
					notation: normalizedDie.notation,
					value,
					type: DICE_TYPES.NORMAL,
					date,
					color: normalizedDie.color,
					label: normalizedDie.label,
				});
				return;
			}

			rerollDice.push(normalizedDie);
		});

		rerollDice.push({
			notation: 6,
			color: 'yellow',
			label: 'stress',
		});

		return {
			retainedSuccesses,
			retainedDiceEntries,
			rerollDice,
			pushed: false,
		};
	};

	const isAlienCharacterLoaded = isAlienCharacter(character);

	const isStressElement = (element) => {
		const normalizedLabel = String(element?.label || '').toLowerCase();
		return (
			element?.key === 'nivel_de_tension' ||
			normalizedLabel === 'nivel de tensión' ||
			normalizedLabel === 'nivel de tension'
		);
	};

	const getCurrentAlienStressLevel = () => {
		if (!character?.sections) {
			return 0;
		}

		const stressElement = character.sections
			.flatMap((section) => section.elements || [])
			.find((element) => isStressElement(element));

		if (typeof stressElement?.value === 'object' && stressElement?.value) {
			return parseInt(String(stressElement.value.value || 0), 10) || 0;
		}

		return parseInt(String(stressElement?.value || 0), 10) || 0;
	};

	const updateAlienCharacterState = async ({
		stressDelta = 0,
		panicResult = null,
		panicEffect = null,
	}) => {
		if (!character || !isAlienCharacterLoaded) {
			return;
		}

		const updatedSections = character.sections.map((section) => ({
			...section,
			elements: section.elements.map((element) => {
				if (!isStressElement(element) || typeof element.value !== 'object' || !element.value) {
					return element;
				}

				return {
					...element,
					value: {
						...element.value,
						value: Math.max(
							element.value.min,
							Math.min(element.value.max, element.value.value + stressDelta)
						),
					},
				};
			}),
		}));

		const updatedStressElement = updatedSections
			.flatMap((section) => section.elements || [])
			.find((element) => isStressElement(element));
		const updatedStressLevel =
			typeof updatedStressElement?.value === 'object' && updatedStressElement?.value
				? parseInt(String(updatedStressElement.value.value || 0), 10) || 0
				: parseInt(String(updatedStressElement?.value || 0), 10) || 0;

		const previousPanicResult = character.systemState?.alien?.activePanicResult || 0;
		let effectivePanicResult = panicResult;
		let effectivePanicEffect = panicEffect;
		if (panicResult != null && previousPanicResult >= 7 && panicResult < previousPanicResult) {
			effectivePanicResult = previousPanicResult + 1;
			effectivePanicEffect = getAlienPanicEntry(effectivePanicResult);
		}

		const updatedCharacter = {
			...character,
			sections: updatedSections,
			systemState: {
				...(character.systemState || {}),
				alien: {
					...(character.systemState?.alien || {}),
					activePanicResult: effectivePanicResult >= 7 ? effectivePanicResult : null,
					activePanicEffect: effectivePanicEffect
						? {
							id: effectivePanicEffect.id,
						}
						: null,
				},
			},
		};

		setCharacter(updatedCharacter);

		if (updatedCharacter.ID) {
			try {
				await updateCharacter(updatedCharacter.ID, updatedCharacter);
			} catch (error) {
				console.error('Error updating Alien character state', error);
			}
		}

		return {
			updatedStressLevel,
			effectivePanicResult,
			effectivePanicEffect,
		};
	};

	const incrementAlienStress = async () => {
		if (!character || !isAlienCharacter(character)) {
			return;
		}
		return updateAlienCharacterState({ stressDelta: 1 });
	};

	const onAlienPush = async (key, pushContext) => {
		closeSnackbar(key);
		const updatedState = await incrementAlienStress();
		const nextStressLevel = updatedState?.updatedStressLevel;
		if (typeof nextStressLevel === 'number') {
			setAlienRollContext((currentContext) =>
				currentContext
					? {
							...currentContext,
							stressLevel: nextStressLevel,
					  }
					: currentContext
			);
		}
		setAlienPushContext({
			...pushContext,
			pushed: true,
		});
		setRoll(pushContext.rerollDice);
	};

	const maybeShowAlienPushMessage = (pushContext) => {
		if (alienPushContext?.pushed) {
			return;
		}

		const currentStressDice = Math.max(
			0,
			pushContext.rerollDice.filter((die) => die.label === 'stress').length - 1
		);
		const nextStressDice = currentStressDice + 1;
		const retainedText =
			pushContext.retainedSuccesses === 1
				? t('diceResults.alien.retainedSingular')
				: t('diceResults.alien.retainedPlural', { count: pushContext.retainedSuccesses });

		enqueueSnackbar(
			t('diceResults.alien.pushPrompt', {
				retainedText,
				currentStress: currentStressDice,
				nextStress: nextStressDice,
			}),
			{
				variant: 'info',
				autoHideDuration: 6000,
				anchorOrigin: { vertical: 'top', horizontal: 'center' },
				action: (key) => (
					<Button onClick={() => onAlienPush(key, pushContext)}>
						{t('diceResults.alien.pushAction')}
					</Button>
				),
			}
		);
	};

	const scheduleAlienPanicRoll = (alienSummary) => {
		const sourceLabel = alienRollContext?.sourceLabel || diceRollMeta?.label || '';

		const launchPanicRoll = () => {
			setAlienRollContext({
				mode: 'panic',
				stressLevel: alienRollContext?.stressLevel ?? getCurrentAlienStressLevel(),
				sourceLabel,
				sourceSuccessCount: alienSummary.successCount ?? alienSummary.value ?? 0,
				sourcePanic: Boolean(alienSummary.panic),
				sourceWasPushed: Boolean(alienSummary.isPushedRoll),
			});
			setRoll([
				{
					notation: 6,
					color: 'red',
					label: 'panic',
				},
			]);
		};

		enqueueSnackbar(t('diceResults.alien.panicTriggered'), {
			variant: 'warning',
			persist: true,
			anchorOrigin: { vertical: 'top', horizontal: 'center' },
			action: (key) => (
				<Button
					onClick={() => {
						closeSnackbar(key);
						setAlienPushContext(null);
						launchPanicRoll();
					}}>
					{t('diceResults.alien.panicAction')}
				</Button>
			),
		});
	};

	const resolveAlienPanicRoll = async (diceDefinitions, values) => {
		const panicDie = values[0] || 0;
		const stressLevel = alienRollContext?.stressLevel ?? getCurrentAlienStressLevel();
		const rawPanicResult = panicDie + stressLevel;
		const panicEffect = getAlienPanicEntry(rawPanicResult);
		const updatedState = await updateAlienCharacterState({
			stressDelta: panicEffect.stressDelta,
			panicResult: rawPanicResult,
			panicEffect,
		});
		if (panicEffect.stressDelta !== 0) {
			const increase = panicEffect.stressDelta > 0;
			enqueueSnackbar(
				t(
					increase
						? 'diceResults.alien.panicStressIncreased'
						: 'diceResults.alien.panicStressDecreased',
					{
						amount: Math.abs(panicEffect.stressDelta),
					}
				),
				{
					variant: increase ? 'warning' : 'info',
					autoHideDuration: 4000,
					anchorOrigin: { vertical: 'top', horizontal: 'center' },
					action: (key) => (
						<Button onClick={() => closeSnackbar(key)}>{t('diceResults.alien.close')}</Button>
					),
				}
			);
		}
		const effectivePanicResult =
			updatedState?.effectivePanicResult != null
				? updatedState.effectivePanicResult
				: rawPanicResult;
		const effectivePanicEffect =
			updatedState?.effectivePanicEffect || panicEffect;

		return [
			{
				id: uuidv4(),
				notation: 'Alien Panic',
				value: effectivePanicResult,
				system: 'alien',
				alienResultType: 'panic',
				rawPanicResult,
				activePanicResult: effectivePanicResult,
				panicRoll: true,
				panicDie,
				stressLevel,
				panicEffectId: effectivePanicEffect.id,
				panicEffect: { id: effectivePanicEffect.id },
				sourceSuccessCount: alienRollContext?.sourceSuccessCount ?? null,
				sourcePanic: Boolean(alienRollContext?.sourcePanic),
				sourceWasPushed: Boolean(alienRollContext?.sourceWasPushed),
				type: DICE_TYPES.CUSTOM,
				date: Date.now(),
			},
		];
	};

	// ── Build each custom‐dice output object ───────────────────────────────────
	const getDicesOutput = (diceDefinitions, results) => {		
		if (customDiceSelected === 'Alien') {
			if (alienRollContext?.mode === 'panic') {
				return resolveAlienPanicRoll(diceDefinitions, results);
			}
			const alienSummary = getAlienSummary(diceDefinitions, results);
				return [
					{
						id: uuidv4(),
						notation: 'Alien Roll',
						value: alienSummary.successes,
						system: 'alien',
						alienResultType: 'skill',
						successCount: alienSummary.successes,
						panic: alienSummary.panic,
						stressOnes: alienSummary.stressOnes,
						isPushedRoll: alienSummary.isPushedRoll,
					type: DICE_TYPES.CUSTOM,
					date: Date.now(),
				},
			];
		}

		return currentCustomDiceConfig.output.map((cfg) => {
			const bonusMap = Object.fromEntries(dicesBonusList.map((b) => [b.name, b.value]));
			const rules = (isNextDiceExplosive ? cfg.explosion : cfg.actions).filter((r) => {
				if (r.if) return r.if.every((c) => bonusMap[c.name] === c.expectedValue);
				return r.default !== false;
			});
			return rules.reduce(
				(cur, rule) => {
					switch (rule.type) {
						case 'dice': return applyOutputDiceAction(cur, results, rule, diceDefinitions);
						case 'modify': return applyOutputModifier(cur, rule.action, rule.name);
						case 'clickable': return clickableDices(cur, rule.action);
						default: return cur;
					}
				},
				{
					id: uuidv4(),
					notation: cfg.notation,
					value: 0,
					type: DICE_TYPES.CUSTOM,
					date: Date.now(),
				}
			);
		});
	};

	// ── Main effect: when dice land, build and push results ────────────────────
	useEffect(() => {
		if (!result || !customDiceSelected || !Array.isArray(roll)) return;

		// 1) figure out which faces were rolled (e.g. [10,10])
		const configuredDice = getConfiguredDiceList();

		// 2) handle re‑roll logic if needed
		let tempValues = [...result];
		let tempRolls = [...roll];
		if (isNextDiceRepetition) {
			const notRerolled = diceResults
				.filter((d) =>
					!repeatDices.some((r) => r.id === d.id) &&
					d.type !== DICE_TYPES.CUSTOM &&
					d.date === repeatDices[0].date
				)
				.map((d) => d);
			tempValues = [...result, ...notRerolled.map((d) => d.value)];
			tempRolls = [
				...roll,
				...notRerolled.map((die) => ({
					notation: die.notation,
					color: die.color || DEFAULT_DICE_COLOR,
					label: normalizeDiceLabel(die.label || DEFAULT_DICE_LABEL),
				})),
			];
		}
		const effectiveDiceDefinitions = tempRolls.map((entry, index) =>
			isSelectedDie(entry) ? toSelectedDie(entry) : configuredDice[index] || toSelectedDie(entry)
		);

		// 3) produce your custom‑dice outputs
		const outOrPromise = getDicesOutput(effectiveDiceDefinitions, tempValues);
		const rollDate = Date.now();

		Promise.resolve(outOrPromise).then((customOut) => {
			// 4) GENERIC three‑tier snackbars for ANY system that set success/partialSuccess
			customOut.forEach((r) => {
				const alienPresentation =
					customDiceSelected === 'Alien'
						? getAlienResultPresentation(r, {
								t,
								locale: i18n.resolvedLanguage || i18n.language,
								userName,
								statLabel: diceRollMeta?.label || '',
						  })
						: null;
				if (alienPresentation) {
					const statInfo = diceRollMeta?.label ? ` (${diceRollMeta.label})` : '';
					r.summary = alienPresentation.summary;
					r.notationMsg = alienPresentation.message;
					r.localizedTitle = alienPresentation.title;
					return;
				}

				if (typeof r.success !== 'boolean' || typeof r.partialSuccess !== 'boolean') {
					return;
				}
				const opts = {
					autoHideDuration: 3000,
					anchorOrigin: { vertical: 'top', horizontal: 'center' },
					action: (key) => <Button onClick={() => closeSnackbar(key)}>Cerrar</Button>,
				};

				let statInfo = '';
				if (diceRollMeta?.label) {
					const sign = diceRollMeta.value >= 0 ? '+' : '';
					statInfo = ` (${diceRollMeta.label} ${sign}${diceRollMeta.value})`;
				}

				const notationMsg = `${r.notation}${statInfo}: ${r.value}`;
				r.notationMsg = `${userName} ha lanzado ${statInfo} con resultado: ${r.value}`;

				if (r.success) {
					enqueueSnackbar(`✅ ${notationMsg}`, { variant: 'success', ...opts });
				} else if (r.partialSuccess) {
					enqueueSnackbar(`⚠️ ${notationMsg}`, { variant: 'warning', ...opts });
				} else {
					enqueueSnackbar(`❌ ${notationMsg}`, { variant: 'error', ...opts });
				}
			});

			// 5) merge raw‑dice + custom outputs into your recoil state
			const newEntries = tempRolls.map((n, i) => ({
				id: uuidv4(),
				notation: isSelectedDie(n) ? n.notation : n,
				value: tempValues[i],
				repeatable: customOut[0]?.repeatable || false,
				type: DICE_TYPES.NORMAL,
				date: rollDate,
				color: isSelectedDie(n) ? n.color || DEFAULT_DICE_COLOR : DEFAULT_DICE_COLOR,
				label: isSelectedDie(n)
					? normalizeDiceLabel(n.label || DEFAULT_DICE_LABEL)
					: DEFAULT_DICE_LABEL,
			}));
			customOut.forEach((entry) => {
				entry.date = rollDate;
			});
			newEntries.push(...customOut);

			if (isNextDiceExplosive) {
				setIsNextDiceExplosive(false);
				setDiceResults([...newEntries, ...diceResults]);
			} else if (isNextDiceRepetition) {
				setIsNextDiceRepetition(false);
				setDiceResults([...newEntries, ...diceResults]);
			} else if (customDiceSelected === 'Alien' && alienRollContext?.mode === 'panic') {
				setDiceResults([...newEntries]);
				setAlienRollContext(null);
			} else if (customDiceSelected === 'Alien' && alienPushContext?.pushed) {
				setDiceResults([...alienPushContext.retainedDiceEntries, ...newEntries]);
				setAlienPushContext(null);
				if (customOut[0]?.panic) {
					scheduleAlienPanicRoll(customOut[0]);
				}
			} else {
				setDiceResults([...newEntries]);
				if (customDiceSelected === 'Alien') {
					if (customOut[0]?.panic) {
						scheduleAlienPanicRoll(customOut[0]);
					} else {
						const nextPushContext = getAlienPushContext(
							effectiveDiceDefinitions,
							tempValues,
							rollDate
						);
						setAlienPushContext(nextPushContext);
						maybeShowAlienPushMessage(nextPushContext);
					}
				}
			}

			// 6) clear trigger
			setTimeout(() => setRoll(0), 1000);
		});
	}, [result]);

	return null;
};

export default React.memo(CustomDiceResults);
